/* ==========================================================================
   Pharmacy Notes Editor
   Markdown-subset editor for the pharmacy-wide note shown to every provider
   (PORTAL-159). A plain Textarea plus a toolbar that inserts Markdown around
   the current selection, Enter continuing bullet/numbered lists the way
   GitHub's editor does, and a Preview tab rendering exactly what providers
   will see via the shared MarkdownText renderer.
   ========================================================================== */

import { useRef, useState, type KeyboardEvent } from 'react'
import { Bold, Italic, Heading, List, ListOrdered, Link2 } from 'lucide-react'
import { Textarea } from '@/components/ui/Textarea'
import { MarkdownText } from '@/components/ui/MarkdownText'

/** Matches the max_length on PharmacyUpdate.pharmacy_notes. */
export const PHARMACY_NOTES_MAX_LENGTH = 10000

type Mode = 'edit' | 'preview'

/** How each toolbar button transforms the selection. */
type Action =
  /** Put `syntax` on both sides of the selection (bold, italic). */
  | { kind: 'wrap'; syntax: string }
  /** Put `syntax` at the start of each selected line (heading, lists). */
  | { kind: 'prefix'; syntax: string | ((index: number) => string) }
  /** Turn the selection into a link. */
  | { kind: 'link' }

interface ToolbarButton {
  label: string
  icon: typeof Bold
  action: Action
  /** Used when nothing is selected, so the button always does something. */
  placeholder: string
}

const TOOLBAR: ToolbarButton[] = [
  { label: 'Bold', icon: Bold, action: { kind: 'wrap', syntax: '**' }, placeholder: 'bold text' },
  { label: 'Italic', icon: Italic, action: { kind: 'wrap', syntax: '*' }, placeholder: 'italic text' },
  { label: 'Heading', icon: Heading, action: { kind: 'prefix', syntax: '## ' }, placeholder: 'Heading' },
  { label: 'Bullet list', icon: List, action: { kind: 'prefix', syntax: '- ' }, placeholder: 'List item' },
  {
    label: 'Numbered list',
    icon: ListOrdered,
    action: { kind: 'prefix', syntax: (i) => `${i + 1}. ` },
    placeholder: 'List item',
  },
  { label: 'Link', icon: Link2, action: { kind: 'link' }, placeholder: 'link text' },
]

/** A list line: indent, then `-`/`*` or `1.`/`1)`, then the item text. */
const LIST_LINE = /^(\s*)(?:([-*])|(\d{1,9})([.)]))\s+(.*)$/

/**
 * What Enter should do at `caret` when the caret sits on a list line: start
 * the next line with the same bullet (or the next number), or, on an item
 * with no text yet, drop the marker so the list ends there. Returns null
 * when the caret is not on a list line, leaving the browser's newline alone.
 */
function continueList(
  value: string,
  caret: number,
): { next: string; caret: number } | null {
  const lineStart = value.slice(0, caret).lastIndexOf('\n') + 1
  const newline = value.indexOf('\n', caret)
  const lineEnd = newline === -1 ? value.length : newline
  const match = LIST_LINE.exec(value.slice(lineStart, lineEnd))
  if (!match) return null
  const [, indent, bullet, number, delimiter, text] = match

  // Only act once the caret is past the marker; before it, a plain newline
  // is what the admin means.
  if (caret < lineStart + match[0].length - text.length) return null

  if (!text.trim()) {
    // Enter on an empty item ends the list: remove the marker, keep the line.
    return { next: value.slice(0, lineStart) + value.slice(lineEnd), caret: lineStart }
  }

  const marker = bullet ? `${bullet} ` : `${Number(number) + 1}${delimiter} `
  const insert = `\n${indent}${marker}`
  return {
    next: value.slice(0, caret) + insert + value.slice(caret),
    caret: caret + insert.length,
  }
}

export interface PharmacyNotesEditorProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

/**
 * Editor for the pharmacy-wide provider note.
 *
 * @example
 * ```tsx
 * <PharmacyNotesEditor value={notes} onChange={setNotes} disabled={saving} />
 * ```
 */
export function PharmacyNotesEditor({
  value,
  onChange,
  disabled = false,
}: PharmacyNotesEditorProps) {
  const [mode, setMode] = useState<Mode>('edit')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  /** Apply a toolbar action to the current selection and restore the caret. */
  function applyAction(button: ToolbarButton) {
    const textarea = textareaRef.current
    if (!textarea) return

    let { selectionStart: start, selectionEnd: end } = textarea

    if (button.action.kind === 'prefix') {
      // Whole-line actions: MarkdownText's block regexes are ^-anchored, so
      // the marker has to land at column 0. Snap the range out to full lines,
      // first dropping a selection's trailing newline so the following
      // (unselected) line isn't pulled in.
      if (end > start && value[end - 1] === '\n') end -= 1
      start = value.slice(0, start).lastIndexOf('\n') + 1
      const lineEnd = value.indexOf('\n', end)
      end = lineEnd === -1 ? value.length : lineEnd
    }

    const selected = value.slice(start, end) || button.placeholder

    let replacement: string
    let selectFrom: number
    let selectTo: number

    if (button.action.kind === 'wrap') {
      const { syntax } = button.action
      replacement = `${syntax}${selected}${syntax}`
      selectFrom = start + syntax.length
      selectTo = selectFrom + selected.length
    } else if (button.action.kind === 'prefix') {
      const { syntax } = button.action
      const lines = selected.split('\n')
      let itemIndex = 0
      replacement = lines
        .map((line) => {
          // Blank lines inside a multi-line selection stay blank rather than
          // becoming empty headings or list items.
          if (lines.length > 1 && !line.trim()) return line
          const prefix = typeof syntax === 'function' ? syntax(itemIndex) : syntax
          itemIndex += 1
          return `${prefix}${line}`
        })
        .join('\n')
      selectFrom = start
      selectTo = start + replacement.length
    } else {
      replacement = `[${selected}](https://)`
      // Drop the caret inside the URL so the address is typed next.
      selectFrom = start + selected.length + 3 + 'https://'.length
      selectTo = selectFrom
    }

    const next = value.slice(0, start) + replacement + value.slice(end)
    if (next.length > PHARMACY_NOTES_MAX_LENGTH) return
    onChange(next)

    // Restore focus/selection after React has written the new value.
    requestAnimationFrame(() => {
      textarea.focus()
      textarea.setSelectionRange(selectFrom, selectTo)
    })
  }

  /** Enter on a list line continues the list; see continueList. */
  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
      return
    }
    if (event.nativeEvent.isComposing) return

    const textarea = event.currentTarget
    if (textarea.selectionStart !== textarea.selectionEnd) return

    const result = continueList(value, textarea.selectionStart)
    if (!result || result.next.length > PHARMACY_NOTES_MAX_LENGTH) return

    event.preventDefault()
    onChange(result.next)
    requestAnimationFrame(() => {
      textarea.setSelectionRange(result.caret, result.caret)
    })
  }

  const remaining = PHARMACY_NOTES_MAX_LENGTH - value.length

  return (
    <div>
      {/* Toolbar + Edit/Preview switch */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="flex items-center gap-1">
          {TOOLBAR.map((button) => (
            <button
              key={button.label}
              type="button"
              onClick={() => applyAction(button)}
              disabled={disabled || mode === 'preview'}
              title={button.label}
              aria-label={button.label}
              className="p-1.5 rounded border border-[var(--color-gray-300)] text-charcoal
                         hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                         transition-colors duration-150"
            >
              <button.icon className="h-4 w-4" aria-hidden="true" />
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-1 text-sm">
          {(['edit', 'preview'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              aria-pressed={mode === m}
              className={`px-2.5 py-1 rounded transition-colors duration-150 ${
                mode === m
                  ? 'bg-[var(--pharmacy-primary)] text-white'
                  : 'text-gray-500 hover:text-charcoal'
              }`}
            >
              {m === 'edit' ? 'Edit' : 'Preview'}
            </button>
          ))}
        </div>
      </div>

      {mode === 'edit' ? (
        <Textarea
          ref={textareaRef}
          id="pharmacy_notes"
          aria-label="Pharmacy notes"
          rows={10}
          value={value}
          maxLength={PHARMACY_NOTES_MAX_LENGTH}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            'e.g.\n## Controlled Substances\n- Schedule II requires a **hard copy**\n- Fax by 4pm for same-day processing'
          }
          className="font-mono text-sm"
        />
      ) : (
        <div className="min-h-[80px] px-3 py-3 border border-[var(--color-gray-300)] rounded-md bg-white">
          {value.trim() ? (
            <MarkdownText text={value} />
          ) : (
            <p className="text-sm text-gray-500">Nothing to preview yet.</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap justify-between gap-2 mt-2">
        <p className="text-sm text-gray-500">
          Use <strong>**bold**</strong>, <em>*italic*</em>, <code>## Heading</code>,{' '}
          <code>- bullets</code>, <code>1. numbered</code> and{' '}
          <code>[links](https://example.com)</code>.
        </p>
        <p
          className={`text-sm ${remaining < 250 ? 'text-[var(--color-error)]' : 'text-gray-500'}`}
        >
          {remaining.toLocaleString()} characters left
        </p>
      </div>
    </div>
  )
}

export default PharmacyNotesEditor
